import React from "react";
import { Dialog, Button, TabbedContent, TabOption } from "ui-components";

import appendix from "@common/appendix";
import AppendixTip from "./AppendixTip";
import AppendixSuggestedTip from "./AppendixSuggestedTip";
import SuggestedTipEditor from "./SuggestedTipEditor";
import {
  AppendixDialogWrapper,
  AppendixDialogHeader,
  AppendixTabbedContentWrapper,
  AppendixTabContent,
  AppendixTabHeader,
  AppendixSubsection,
  AppendixSubsectionHeader,
  AppendixTipWrapper,
  AppendixDialogButtons
} from "./styled";

const AppendixDialog = ({
  subsectionIdx,
  title,
  isShown,
  toggleDialog,
  section,
  onAppendixTipClick,
  onSuggestedTipClick,
  upsertSuggestedTip,
  deleteSuggestedTip
}) => {
  // The dialog should only be rendered when shown
  if (!isShown) {
    return null;
  }

  const renderTabOptions = section => {
    const tabs = [];

    // Get all suggested tips that are associated with the indicated risks in the section
    const suggestedTips = section.risks.reduce((accum, risk) => {
      return risk.isRisk ? accum.concat(risk.suggestedTips) : accum;
    }, []);

    // Get the custom suggested tips that the user has added
    const customSuggestedTips = section.suggestedTips;

    tabs.push(
      <TabOption
        title="Suggested Controls"
        key={`tab_sc`}
        tooltip="Suggested Controls"
      >
        <AppendixTabContent>
          <AppendixTipWrapper>
            <SuggestedTipEditor
              tipInfo={{
                stepIdx: subsectionIdx
              }}
              editorPlaceHolder="Add new suggested controls"
              upsertSuggestedTip={upsertSuggestedTip}
            />
          </AppendixTipWrapper>
          {customSuggestedTips.map((tip, tipIdx) => {
            // Necessary information for each tip (for click handler & actions)
            const tipInfo = {
              stepIdx: subsectionIdx,
              tipIdx,
              tipText: tip.text
            };

            return (
              <AppendixSuggestedTip
                key={`sc_${tipIdx}`}
                tipInfo={tipInfo}
                isCustom={tip.isCustom}
                isChecked={tip.isChecked}
                onClick={() => onSuggestedTipClick(tipInfo)}
                upsertSuggestedTip={upsertSuggestedTip}
                deleteSuggestedTip={deleteSuggestedTip}
              >
                {tip.text}
              </AppendixSuggestedTip>
            );
          })}
          {suggestedTips.map((tip, tipIdx) => (
            <AppendixSuggestedTip
              key={`sc_${tipIdx}`}
              references={tip.references}
              isCustom={false}
              isChecked={true}
            >
              {tip.text}
            </AppendixSuggestedTip>
          ))}
        </AppendixTabContent>
      </TabOption>
    );

    // Get the appendix sections that apply to the specific risk
    const appendixTabs = section.appendixSections.map(appendixKey => {
      // Get corresponding appendix section based on the current appendix key
      const appendixSection = appendix[appendixKey];

      return (
        <TabOption
          title={appendixKey.toUpperCase()}
          key={`tab_${appendixKey}`}
          tooltip={appendixSection.title}
        >
          <AppendixTabHeader>
            {appendixSection.title}
          </AppendixTabHeader>
          <AppendixTabContent>
            {appendixSection.sections.map(
              (appendixSubsection, appendixSubsectionIdx) => {
                return (
                  <AppendixSubsection key={`asub_${appendixSubsectionIdx}`}>
                    {appendixSubsection.subTitle.length > 0
                      ? <AppendixSubsectionHeader>
                          {appendixSubsection.subTitle}
                        </AppendixSubsectionHeader>
                      : null}
                    {appendixSubsection.tips.map((tip, tipIdx) => {
                      const tipKey = `${appendixKey}_${tipIdx}`;

                      // Necessary information for each tip (for click handler & actions)
                      const tipInfo = {
                        stepIdx: subsectionIdx,
                        appendixSectionKey: appendixKey,
                        appendixSubsectionIdx: appendixSubsectionIdx,
                        tipIdx
                      };

                      // Determine if appendix tip is checked
                      const isChecked = section.checkedAppendixTips[
                        tipInfo.appendixSectionKey
                      ][tipInfo.appendixSubsectionIdx].includes(tipIdx);

                      return (
                        <AppendixTip
                          key={tipKey}
                          tipInfo={tipInfo}
                          isChecked={isChecked}
                          onClick={() => onAppendixTipClick(tipInfo)}
                        >
                          {tip}
                        </AppendixTip>
                      );
                    })}
                  </AppendixSubsection>
                );
              }
            )}
          </AppendixTabContent>
        </TabOption>
      );
    });

    return tabs.concat(appendixTabs);
  };

  return (
    <Dialog display={isShown}>
      <AppendixDialogWrapper>
        <AppendixDialogHeader>
          Risk Specific Guidelines and Tips
        </AppendixDialogHeader>
        <AppendixTabbedContentWrapper>
          <TabbedContent>
            {renderTabOptions(section)}
          </TabbedContent>
        </AppendixTabbedContentWrapper>
        <AppendixDialogButtons>
          <Button key="close" medium onClick={toggleDialog}>
            Done
          </Button>
        </AppendixDialogButtons>
      </AppendixDialogWrapper>
    </Dialog>
  );
};

export default AppendixDialog;



// WEBPACK FOOTER //
// ./src/common/components/AppendixDialog/AppendixDialog.jsx